'use client';

import { useEffect, useState } from 'react';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import {
  SavingsPlanTransactionItemType,
  StakingTransactionItemType,
} from '@bts-web/core-features/transactions';
import { useHandleGqlErrorsWithSnackbar } from '../../common';
import {
  transactionTypeByTypeName,
  TransactionTypes,
} from '../../transactions/types/transactions';
import { datadogErrorHelper } from '../../common/utils/datadogErrorHelper';
import { getTransactionDetailsDataByType } from '../utils';
import {
  CryptoActionViewQueryData,
  FiatTransactionViewQueryData,
  SwapOfferViewQueryData,
  TransactionViewQueryData,
} from '../types';

export const useTransactionDetails = ({
  tradeId,
  transactionType,
}: {
  tradeId: string;
  transactionType: TransactionTypes;
}) => {
  const [isTransactionDataFetching, setTransactionDataFetching] =
    useState(true);

  const isFetchedByExternalTransactionId =
    transactionType ===
    transactionTypeByTypeName.InternalSavingsPlanTransaction;

  const [transaction, setTransaction] = useState<
    | SwapOfferViewQueryData
    | TransactionViewQueryData
    | SavingsPlanTransactionItemType
    | StakingTransactionItemType
    | CryptoActionViewQueryData
    | FiatTransactionViewQueryData
    | null
  >(null);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const fetchTransactionData = async () => {
    try {
      const { errors, transaction } = await getTransactionDetailsDataByType({
        id: tradeId,
        type: transactionType,
      });

      if (errors) {
        processErrors(errors);
      } else {
        setTransaction(transaction || null);
      }
    } catch (error) {
      datadogErrorHelper({
        errorMessage: JSON.stringify(error),
        errorSeverity: DatadogErrorLevels.HIGH,
      });
    } finally {
      setTransactionDataFetching(false);
    }
  };

  useEffect(() => {
    setTransactionDataFetching(true);

    fetchTransactionData();
  }, [tradeId, isFetchedByExternalTransactionId]);

  return { isTransactionFetching: isTransactionDataFetching, transaction };
};
